import { useEffect, useState } from "react";
import { pendingChangeText } from "../lib/soaApproval";
import { useSearchParams } from "react-router-dom";
import { Shield } from "lucide-react";
import { theme, card, emptyFrame, type as text } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { ControlInsightsPanel, buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { AiRecommendations } from "../components/AiRecommendations";
import { useControlTagData, useTagFilter, matchesTagFilter } from "../components/ControlTags";
import { DetailSection } from "../components/RecordView";
import { RequirementSatisfaction } from "../components/RequirementSatisfaction";
import { StatusTag } from "../components/Drawing";
import { frameworkAccent } from "../components/controlStatus";
import { splitLeadingRef } from "../lib/controlRef";
import { CONTROL_THEMES, type ControlDef } from "../data/cyberEssentialsControls";
import { StandardHeader, GuidedIntro, StandardNote, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { StatusSummary, countStatuses } from "../components/standards/StatusSummary";
import { ControlFilterBar, statusFilterOptions, matchesControlSearch, isGroupOpen, toggleGroup, type GroupExpansion } from "../components/standards/ControlFilterBar";
import { ControlGroup, ControlGroupStack } from "../components/standards/ControlGroup";
import { ControlRow } from "../components/standards/ControlRow";
import { ControlDetail, evidenceCountFor, type StatusOption } from "../components/standards/ControlDetail";
import { useStandardPermissions } from "../components/standards/useStandardPermissions";
import { fmtDateOnly } from "../lib/dateOnly";
import { useRecordChecks } from "../components/RecordChecks";

// Cyber Essentials and Cyber Essentials Plus: the five technical themes of the requirements for
// IT infrastructure. Each requirement is a Control row (frameworkId "cyber_essentials", id = the key
// below, shared by CE and CE+) and the organisation's answer is its SoaEntry, read through
// GET /controls/soa and written through PUT /controls/soa/:id like every other standard's — the same
// consultant approval, and an AI "compliant" waits at in progress for a person to confirm it.
// Presented exactly as every other standard: summary, filter bar, one group per theme, one row per
// requirement and the shared detail. The Jersey edition renders this page too.

/** The framework whose Control rows the requirements are; CE+ is assessed against the same rows. */
const CE_ROWS = "cyber_essentials";

interface CeEntry {
  status: string;
  justification: string | null;
  aiNote?: string | null;
  approvalStatus: string;
  applicable?: boolean | null;
  approvedStatus?: string | null;
  approvedApplicable?: boolean | null;
  approvedJustification?: string | null;
  rejectionReason?: string | null;
}

/** Mirror of backend lib/ceConsistency.ts: where the platform's records contradict an answer. */
interface CeConsistency {
  cePlusFailures: Record<string, Array<{ testKey: string; title: string; testedAt: string | null; remediationDueAt: string | null }>>;
  patchingContradictions: Array<{ requirement: string; ref: string; claimedBy: string[]; breaching: number; oldestDays: number; examples: Array<{ cveId: string; assetName: string; days: number }> }>;
}

interface EvidenceDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  controls: Array<{ frameworkId: string; controlRef: string }>;
}

const ALL_CONTROLS: ControlDef[] = CONTROL_THEMES.flatMap((t) => t.controls);

function ceStatusOptions(novice: boolean): StatusOption[] {
  return [
    { value: "NOT_STARTED", label: novice ? "Not started yet" : "Not assessed" },
    { value: "IN_PROGRESS", label: novice ? "Working on it" : "In progress" },
    { value: "GAP", label: novice ? "Need to fix this" : "Gap identified" },
    { value: "COMPLIANT", label: novice ? "Already in place" : "Request compliant" },
  ];
}

export function CyberEssentialsPage({ plus = false }: { plus?: boolean }) {
  const { novice } = useExperience();
  const { canAssess, canAttach } = useStandardPermissions();
  const recordChecks = useRecordChecks();
  const fwId = plus ? "cyber_essentials_plus" : "cyber_essentials";
  const accent = frameworkAccent(fwId);
  const [entries, setEntries] = useState<Map<string, CeEntry>>(new Map());
  const [consistency, setConsistency] = useState<CeConsistency | null>(null);
  // My Actions links a flagged requirement here (?control=<key>).
  const [searchParams] = useSearchParams();
  const linkedControl = searchParams.get("control");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [themeFilter, setThemeFilter] = useState("all");
  const [expansion, setExpansion] = useState<GroupExpansion>("all");
  // Department / office / jurisdiction tags (components/ControlTags.tsx), keyed by the CE control key.
  const tagData = useControlTagData();
  const [tagFilter, setTagFilter] = useTagFilter();

  async function loadDocs() {
    const { documents } = await api.get<{ documents: EvidenceDoc[] }>("/documents");
    setDocs(documents);
  }

  // Failed CE+ tests and contradicted patching answers (lib/ceConsistency.ts); the page works without them.
  useEffect(() => {
    api.get<CeConsistency>("/ce/consistency").then(setConsistency).catch(() => setConsistency(null));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get<{ soa: Array<{ control: { id: string; frameworkId: string }; entry: CeEntry }> }>("/controls/soa"),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
      api.get<{ documents: EvidenceDoc[] }>("/documents").catch(() => ({ documents: [] })),
    ]).then(([d, latestRun, docsData]) => {
      setEntries(new Map(d.soa.filter((r) => r.control.frameworkId === CE_ROWS).map((r) => [r.control.id, r.entry])));
      if (linkedControl && ALL_CONTROLS.some((c) => c.key === linkedControl)) setOpen(linkedControl);
      if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
      setDocs(docsData.documents);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // The same write as every standard's SOA (PUT /controls/soa/:id): the server decides whether the
  // change waits for consultant approval (lib/soaApproval.ts) and returns the entry as stored.
  async function saveEntry(controlKey: string, patch: { status?: string; justification?: string }) {
    const { entry } = await api.put<{ entry: CeEntry }>(`/controls/soa/${controlKey}`, patch);
    setEntries((prev) => new Map(prev).set(controlKey, { ...prev.get(controlKey), ...entry }));
  }

  /** What the platform's own records say against this requirement; the answer itself is never changed. */
  function flagsFor(key: string) {
    const failures = consistency?.cePlusFailures[key] ?? [];
    const contradiction = consistency?.patchingContradictions.find((c) => c.requirement === key) ?? null;
    if (!failures.length && !contradiction) return null;
    return (
      <DetailSection title="Your records disagree">
        {failures.map((f) => (
          <div key={f.testKey} style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 6 }}>
            The CE+ assessor recorded a fail on "{f.title}"{f.testedAt ? ` on ${fmtDateOnly(f.testedAt)}` : ""}. Remediate and have it re-verified
            {f.remediationDueAt ? ` by ${fmtDateOnly(f.remediationDueAt)}` : " within 30 days of the test"}; a corrective action has been raised.
          </div>
        ))}
        {contradiction && (
          <div style={{ ...text.hint, color: theme.color.dangerText }}>
            {contradiction.ref} is answered "yes"{contradiction.claimedBy.includes("intake") ? " (also in your intake answers)" : ""}, but the vulnerability register holds {contradiction.breaching} critical or high finding{contradiction.breaching === 1 ? "" : "s"} open for more than 14 days
            (oldest {contradiction.oldestDays} days: {contradiction.examples.slice(0, 3).map((e) => `${e.cveId} on ${e.assetName}`).join(", ")}).
            Fix them, record a risk-accepted exception on the vulnerability, or correct the answer — the platform does not change it for you.
          </div>
        )}
      </DetailSection>
    );
  }

  async function updateStatus(controlKey: string, status: string) {
    setSaving(controlKey);
    setSaveError("");
    try {
      await saveEntry(controlKey, { status });
    } catch (e) {
      // Shown, never swallowed: the editor otherwise stays open as if the change had been made.
      setSaveError(e instanceof Error && e.message ? e.message : "The status was not saved.");
    } finally {
      setSaving(null);
    }
  }

  // Mirror the newest note into the entry's justification for AI insights and reports
  // (capped at 2000 characters; the thread keeps the full text).
  async function mirrorNote(controlKey: string, body: string) {
    try {
      await saveEntry(controlKey, { justification: body.slice(0, 2000) });
    } catch { /* the note itself is saved; the mirror is best-effort */ }
  }

  if (loading) return <LoadingBlock />;

  const rowFor = (key: string) => entries.get(key);
  const statusOf = (key: string) => rowFor(key)?.status ?? "NOT_STARTED";

  const themes = CONTROL_THEMES.map((t) => {
    const { reference, title } = splitLeadingRef(t.title);
    return { ...t, groupTitle: reference ? `${reference} ${title}` : title };
  });
  const visibleThemes = themes
    .filter((t) => themeFilter === "all" || t.id === themeFilter)
    .map((t) => ({
      ...t,
      controls: t.controls.filter((ctrl) => {
        const { reference, title } = splitLeadingRef(ctrl.title);
        return (statusFilter === "all" || statusOf(ctrl.key) === statusFilter)
          && matchesTagFilter(tagData, ctrl.key, tagFilter)
          && matchesControlSearch(search, reference, title);
      }),
    }))
    .filter((t) => t.controls.length > 0);
  const shown = visibleThemes.reduce((n, t) => n + t.controls.length, 0);
  const searching = search.trim().length > 0;

  function renderRow(ctrl: ControlDef) {
    const row = rowFor(ctrl.key);
    const status = statusOf(ctrl.key);
    const isOpen = open === ctrl.key;
    const { reference, title } = splitLeadingRef(ctrl.title);
    const insights = insightsMap.get(ctrl.key) ?? null;
    const pending = row?.approvalStatus === "DRAFT";
    const notes = row?.justification ?? undefined;
    return (
      <ControlRow
        key={ctrl.key}
        reference={reference}
        title={title}
        subtitle={!isOpen ? ctrl.description : undefined}
        extra={(consistency?.cePlusFailures[ctrl.key]?.length || consistency?.patchingContradictions.some((c) => c.requirement === ctrl.key))
          ? <StatusTag tone="gap" title="A CE+ test failed or the vulnerability register contradicts this answer">Records disagree</StatusTag>
          : undefined}
        status={status}
        novice={novice}
        pending={pending}
        evidenceCount={evidenceCountFor(docs, fwId, ctrl.key)}
        hasNotes={!!notes}
        open={isOpen}
        onToggle={() => setOpen(isOpen ? null : ctrl.key)}
      >
        <ControlDetail
          controlId={ctrl.key}
          requirement={ctrl.description}
          checklist={ctrl.guidance}
          checklistLabel="What is required"
          sections={<>
            {flagsFor(ctrl.key)}
            {plus && (
              <DetailSection title="Cyber Essentials Plus">
                <div style={text.hint}>Your assessor will verify this requirement independently through technical testing.</div>
              </DetailSection>
            )}
            {/* Re-keyed on status so a change on the row re-reads what evidences it. */}
            <RequirementSatisfaction key={`${ctrl.key}:${status}`} controlId={ctrl.key} />
          </>}
          status={status}
          novice={novice}
          pending={pending}
          pendingText={row ? pendingChangeText({ applicable: null, ...row }) : (novice ? "Your consultant is reviewing this" : "Awaiting consultant approval")}
          rejectionReason={row?.rejectionReason}
          statusLabel={novice ? "How are we doing with this?" : "Your status"}
          aiNote={row?.aiNote}
          recordChecks={recordChecks[ctrl.key]}
          statusOptions={ceStatusOptions(novice)}
          onSetStatus={(s) => updateStatus(ctrl.key, s)}
          saving={saving === ctrl.key}
          canEdit={canAssess}
          evidence={{ docs, frameworkId: fwId, controlRef: ctrl.key, reload: loadDocs }}
          canAttach={canAttach}
          notes={{
            // One thread per requirement, shared by CE and CE+, keyed by its Control id as on every standard.
            threadId: ctrl.key,
            label: novice ? "Your notes and evidence" : "Notes and evidence",
            placeholder: novice ? "Describe how you meet this requirement, or note what you still need to do…" : "Document your implementation, evidence locations, or gaps…",
            legacyNote: row?.justification,
            onPost: (body) => { void mirrorNote(ctrl.key, body); },
          }}
          insights={insights
            ? <ControlInsightsPanel insights={insights} controlId={ctrl.key} controlName={ctrl.title} framework={fwId} status={status} notes={notes} guidance={ctrl.guidance} />
            : <AiRecommendations controlId={ctrl.key} controlName={ctrl.title} framework={fwId} status={status} notes={notes} guidance={ctrl.guidance} />}
        />
      </ControlRow>
    );
  }

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId={fwId}
        page={STANDARD_PAGE_LABELS.assessment}
        subtitle={plus
          ? "Self-assess every requirement across the five themes, then arrange the hands-on technical verification with your certification body."
          : "The NCSC-backed certification of five themes of foundational technical controls: self-assess each requirement below."}
      />

      {saveError && (
        <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{saveError}</div>
      )}

      {novice && (
        <GuidedIntro title="How to complete your assessment" icon={<Shield size={18} />}>
          Work through each requirement below. Open any item to see exactly what is required, then mark your status honestly; your consultant can help if you are unsure. Once everything shows <strong style={{ color: theme.color.text }}>We've done this</strong>, you are ready to apply for certification.
        </GuidedIntro>
      )}

      <StatusSummary
        label="Requirements compliant"
        counts={countStatuses(ALL_CONTROLS.map((c) => statusOf(c.key)))}
        novice={novice}
      >
        {plus && (
          <span style={{ ...text.meta, alignSelf: "center", padding: "6px 12px", background: theme.color.accentBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.accentText }}>
            Independent verification required
          </span>
        )}
      </StatusSummary>

      <ControlFilterBar
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        statusOptions={statusFilterOptions(novice)}
        group={themeFilter}
        onGroup={setThemeFilter}
        groupOptions={themes.map((t) => ({ value: t.id, label: t.groupTitle }))}
        groupLabel="All themes"
        tagFilter={tagFilter}
        onTagFilter={setTagFilter}
        accent={accent}
        count={{ shown, total: ALL_CONTROLS.length, noun: "requirements" }}
        onExpandAll={() => setExpansion("all")}
        onCollapseAll={() => setExpansion("none")}
      />

      {visibleThemes.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "40px 20px", ...text.hint }}>No requirements match these filters.</div>
      ) : (
        <ControlGroupStack>
          {visibleThemes.map((t) => (
            <ControlGroup
              key={t.id}
              title={t.groupTitle}
              accent={accent}
              count={t.controls.length}
              noun="requirement"
              compliant={t.controls.filter((c) => statusOf(c.key) === "COMPLIANT").length}
              gap={t.controls.filter((c) => statusOf(c.key) === "GAP").length}
              expanded={searching || isGroupOpen(expansion, t.id)}
              onToggle={() => setExpansion((prev) => toggleGroup(prev, t.id, themes.map((x) => x.id)))}
            >
              {t.controls.map(renderRow)}
            </ControlGroup>
          ))}
        </ControlGroupStack>
      )}

      {plus && <CePlusVerificationSummary />}
    </div>
  );
}

// ── Cyber Essentials Plus technical verification (read-only) ─────────────
// What the certification body engaged for CE+ has recorded of its technical audit
// (GET /ce/plus-verification; backend lib/cePlusTests.ts). Only the assessor writes
// it; here it is read, never edited.

type CePlusResult = "pass" | "fail" | "not_tested";

interface CePlusVerification {
  bureau: { id: string; name: string } | null;
  summary: { outcome: "passed" | "remediation_required" | "in_progress" | "not_started"; passed: number; failed: number; notTested: number; total: number; remediationDueAt: string | null };
  tests: Array<{ key: string; area: string; title: string; description: string; result: CePlusResult; sample: string | null; notes: string | null; testedAt: string | null; remediationDueAt: string | null }>;
}

const CE_PLUS_RESULT_LABELS: Record<CePlusResult, string> = { pass: "Pass", fail: "Fail", not_tested: "Not tested" };
const CE_PLUS_OUTCOME_LABELS: Record<CePlusVerification["summary"]["outcome"], string> = {
  passed: "Every test passed",
  remediation_required: "Remediation required",
  in_progress: "Testing in progress",
  not_started: "Testing not started",
};

function CePlusVerificationSummary() {
  const [data, setData] = useState<CePlusVerification | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<CePlusVerification>("/ce/plus-verification")
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const arrangeNote = (
    <>
      Once all requirements show <strong>Compliant</strong>, contact your certification body to arrange the CE+
      technical verification. An assessor will independently test each control, typically including
      vulnerability scanning of internet-facing systems, internal device sampling, and review of your
      configuration evidence.
    </>
  );

  if (failed || !data || !data.bureau || data.summary.outcome === "not_started") {
    return (
      <StandardNote title="Independent verification (CE+)">
        {arrangeNote}
        {data?.bureau && <> {data.bureau.name} has not recorded any test results yet.</>}
      </StandardNote>
    );
  }

  const { summary } = data;
  const outcomeColour = summary.outcome === "passed" ? theme.color.successText : summary.outcome === "remediation_required" ? theme.color.dangerText : theme.color.muted;
  return (
    <div style={{ ...card, marginTop: 20 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <div style={{ ...text.sectionTitle, flex: 1 }}>Technical verification by {data.bureau.name}</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: outcomeColour }}>{CE_PLUS_OUTCOME_LABELS[summary.outcome]}</span>
      </div>
      <div style={{ ...text.hint, marginTop: 4 }}>
        {summary.passed} passed · {summary.failed} failed · {summary.notTested} not tested. Every test must pass for Cyber Essentials Plus;
        a fail has to be fixed and re-tested within 30 days of the test.
        {summary.remediationDueAt && <strong style={{ color: theme.color.dangerText }}> Earliest deadline {fmtDateOnly(summary.remediationDueAt)}.</strong>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", marginTop: 12 }}>
        {data.tests.map((t, i) => (
          <div key={t.key} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "8px 0", borderTop: i === 0 ? "none" : `1px solid ${theme.color.border}`, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{t.title}</div>
              {t.notes && <div style={{ ...text.hint, marginTop: 2, whiteSpace: "pre-wrap" }}>{t.notes}</div>}
              {(t.testedAt || t.remediationDueAt) && (
                <div style={{ ...text.meta, marginTop: 2 }}>
                  {t.testedAt ? `Tested ${fmtDateOnly(t.testedAt)}` : ""}
                  {t.remediationDueAt ? ` · fix and re-test by ${fmtDateOnly(t.remediationDueAt)}` : ""}
                </div>
              )}
            </div>
            <StatusTag tone={t.result === "pass" ? "met" : t.result === "fail" ? "gap" : "none"}>
              {CE_PLUS_RESULT_LABELS[t.result]}
            </StatusTag>
          </div>
        ))}
      </div>
    </div>
  );
}
